import { useTranslation } from 'react-i18next';

interface TocItem {
  label: string;
  children?: string[];
}

interface Chapter {
  title: string;
  items: TocItem[];
}

function parseItems(raw: string): TocItem[] {
  return raw
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const separatorIndex = part.indexOf('→');
      if (separatorIndex === -1) {
        return { label: part };
      }
      return {
        label: part.slice(0, separatorIndex).trim(),
        children: part
          .slice(separatorIndex + 1)
          .split('¦')
          .map((child) => child.trim())
          .filter(Boolean),
      };
    });
}

export default function TocSection() {
  const { t } = useTranslation();

  const chapters: Chapter[] = [
    { title: t('toc_ch1_title'), items: parseItems(t('toc_ch1_items')) },
    { title: t('toc_ch2_title'), items: parseItems(t('toc_ch2_items')) },
    { title: t('toc_ch3_title'), items: parseItems(t('toc_ch3_items')) },
    { title: t('toc_ch4_title'), items: parseItems(t('toc_ch4_items')) },
    { title: t('toc_ch5_title'), items: parseItems(t('toc_ch5_items')) },
    { title: t('toc_ch6_title'), items: parseItems(t('toc_ch6_items')) },
  ];

  return (
    <section id="contents" className="py-10 md:py-12 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-8 md:mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
              {t('toc_title')}
            </h2>
            <div className="w-14 h-1 bg-accent-500 mx-auto mt-5 rounded-full" />
          </div>

          {/* Chapters */}
          <div className="space-y-4">
            {chapters.map((chapter, chIndex) => (
              <div
                key={chIndex}
                className="bg-background-50 border border-background-200/70 rounded-lg"
              >
                {/* Chapter header */}
                <div className="px-4 md:px-6 py-2.5 md:py-3 border-b border-background-200/70">
                  <div className="flex items-center gap-2.5">
                    <span className="text-accent-500 font-heading text-base md:text-lg font-semibold leading-none">
                      {String(chIndex + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-heading text-sm md:text-base font-medium text-foreground-900 leading-snug">
                      {chapter.title}
                    </h3>
                  </div>
                </div>

                {/* Chapter items */}
                <div className="px-4 md:px-6 py-2.5 md:py-3">
                  <ul className="space-y-2">
                    {chapter.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        {item.children && item.children.length > 0 ? (
                          <div>
                            <div className="flex items-start gap-2.5">
                              <span className="text-secondary-400 font-semibold text-xs flex-shrink-0 w-5 text-right mt-0.5">
                                {itemIndex + 1}.
                              </span>
                              <span className="text-foreground-900 font-medium text-xs md:text-sm leading-relaxed">
                                {item.label}
                              </span>
                            </div>
                            <ul className="mt-1.5 space-y-1.5 pl-[1.875rem]">
                              {item.children.map((child, childIndex) => (
                                <li
                                  key={childIndex}
                                  className="flex items-start gap-2.5"
                                >
                                  <span className="text-secondary-400/70 font-semibold text-xs flex-shrink-0 w-8 text-right mt-0.5">
                                    {itemIndex + 1}.{childIndex + 1}
                                  </span>
                                  <span className="text-foreground-700 text-xs md:text-sm leading-relaxed">
                                    {child}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2.5">
                            <span className="text-secondary-400 font-semibold text-xs flex-shrink-0 w-5 text-right mt-0.5">
                              {itemIndex + 1}.
                            </span>
                            <span className="text-foreground-700 text-xs md:text-sm leading-relaxed">
                              {item.label}
                            </span>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}